import {
  CampaignDetails,
  EngagementMessage,
  EngagementMessagesSchedules,
  MessagesReport,
  MessagesReportDetails,
} from '../types/campaignDetails';
import { formatCount, formatCountdown, formatWhen, toDate } from '../../../../../utils/sms';
import type { MessageMeta } from '../../../../services/campaign/campaign-insights.service';

/**
 * Helpers shared by the campaign details pages: when each scheduled send goes
 * out (in its own time zone), the campaign's sends in order, and the small
 * labels the pages repeat.
 */

/** One scheduled send of one campaign message. */
export interface CampaignSend {
  key: string;
  message: EngagementMessage;
  schedule: EngagementMessagesSchedules;
  subject: string;
  /** The moment it goes out, or null when the schedule has no usable date. */
  at: Date | null;
  /** "Today, 14:05" in the viewer's time. */
  when: string;
  /** The schedule as entered, with its zone, for a tooltip. */
  exact: string;
  /** Shown when the schedule's zone differs from the viewer's. */
  zone: string | null;
  past: boolean;
  /** Schedule status, else the report's status for this send. */
  status: string;
  /** The campaign report's row for this send, when one matches. */
  report: MessagesReportDetails | null;
}

const VIEWER_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
})();

/** Messages in the order they were added (the API returns an unordered set). */
export function orderedMessages(campaign: CampaignDetails | null | undefined): EngagementMessage[] {
  const messages = Array.isArray(campaign?.engagementMessages) ? campaign!.engagementMessages : [];
  return [...messages].filter(Boolean).sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
}

/**
 * The message with what its own GET knows (uuid, sender, status, send time)
 * filled in where the campaign details leave them out. A copy for display
 * only: never put it in a payload.
 */
export function withMeta(message: EngagementMessage, meta: MessageMeta | null | undefined): EngagementMessage {
  if (!meta) return message;
  return {
    ...message,
    uuid: message.uuid || meta.uuid,
    from: message.from || meta.from,
    status: message.status || meta.status,
    sendAt: message.sendAt || meta.sendAt,
  };
}

/** orderedMessages with each message's own details filled in (display only). */
export function displayMessages(
  campaign: CampaignDetails | null | undefined,
  meta: Record<number, MessageMeta> | null | undefined
): EngagementMessage[] {
  return orderedMessages(campaign).map((message) => withMeta(message, meta?.[message.id]));
}

export function schedulesOf(message: EngagementMessage | null | undefined): EngagementMessagesSchedules[] {
  return Array.isArray(message?.engagementMessageSchedules)
    ? message!.engagementMessageSchedules.filter(Boolean)
    : [];
}

/**
 * The instant a schedule goes out. The date and time are wall-clock values in
 * the schedule's zone, so they are converted from that zone when it is known.
 */
export function scheduleMoment(schedule: Partial<EngagementMessagesSchedules> | null | undefined): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(schedule?.scheduleDate ?? '');
  if (!day) return toDate(schedule?.scheduleDate ?? null);
  const clock = /^(\d{1,2}):(\d{2})/.exec(schedule?.scheduleTime ?? '') ?? [null, '0', '0'];
  const parts = [Number(day[1]), Number(day[2]) - 1, Number(day[3]), Number(clock[1]), Number(clock[2])] as const;

  const zone = schedule?.scheduleTimeZone;
  if (zone) {
    const zoned = fromZonedTime(parts, zone);
    if (zoned) return zoned;
  }
  return new Date(parts[0], parts[1], parts[2], parts[3], parts[4]);
}

/** Wall-clock parts in an IANA zone → the instant. Null for an unknown zone. */
function fromZonedTime(
  [year, month, day, hour, minute]: readonly [number, number, number, number, number],
  zone: string
): Date | null {
  try {
    const format = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
    const offsetAt = (instant: number) => {
      const read: Record<string, number> = {};
      for (const part of format.formatToParts(new Date(instant))) {
        if (part.type !== 'literal') read[part.type] = Number(part.value);
      }
      const asUtc = Date.UTC(read['year'], read['month'] - 1, read['day'], read['hour'] % 24, read['minute']);
      return asUtc - Math.floor(instant / 60000) * 60000;
    };
    const guess = Date.UTC(year, month, day, hour, minute);
    let instant = guess - offsetAt(guess);
    // A second pass settles times near a daylight-saving change.
    instant = guess - offsetAt(instant);
    return new Date(instant);
  } catch {
    return null;
  }
}

/** The schedule exactly as entered: "13 Oct 2026, 09:00 Africa/Gaborone". */
function exactSchedule(schedule: EngagementMessagesSchedules): string {
  const date = schedule.scheduleDate ? schedule.scheduleDate.slice(0, 10) : '';
  const time = schedule.scheduleTime ? schedule.scheduleTime.slice(0, 5) : '';
  return [date, time, schedule.scheduleTimeZone ?? ''].filter(Boolean).join(' ');
}

/** A report row's send time as a local date ("2026-10-13T09:00:00" or similar). */
function reportMoment(row: MessagesReportDetails): Date | null {
  return toDate(row?.messageSchedules ?? null);
}

function sameMinute(a: Date | null, b: Date | null): boolean {
  return !!a && !!b && Math.floor(a.getTime() / 60000) === Math.floor(b.getTime() / 60000);
}

/**
 * Every send in the campaign, soonest first, each matched to its row in the
 * campaign report when possible: by schedule uuid, else by send time, else by
 * position when the message has as many report rows as schedules.
 */
export function campaignSends(
  campaign: CampaignDetails | null | undefined,
  reports: MessagesReport[] = [],
  now = new Date(),
  meta?: Record<number, MessageMeta> | null
): CampaignSend[] {
  const sends: CampaignSend[] = [];

  // The report is keyed by message uuid, which only the message's own GET carries.
  for (const message of displayMessages(campaign, meta)) {
    const schedules = schedulesOf(message);
    const report = reports.find((r) => !!message.uuid && r?.referenceId === message.uuid);
    const rows = Array.isArray(report?.reports) ? report!.reports.filter(Boolean) : [];
    const sortedRows = [...rows].sort(
      (a, b) => (reportMoment(a)?.getTime() ?? 0) - (reportMoment(b)?.getTime() ?? 0)
    );
    const byPosition = sortedRows.length === schedules.length;
    const sortedSchedules = [...schedules].sort(
      (a, b) => (scheduleMoment(a)?.getTime() ?? 0) - (scheduleMoment(b)?.getTime() ?? 0)
    );

    sortedSchedules.forEach((schedule, index) => {
      const at = scheduleMoment(schedule);
      // The wall-clock time without zone conversion, as the report stores it.
      const wall = scheduleMoment({ ...schedule, scheduleTimeZone: null });
      const uuid = (schedule as { uuid?: string | null }).uuid;
      const match =
        (uuid ? rows.find((row) => row.scheduleUuid === uuid) : undefined) ??
        rows.find((row) => sameMinute(reportMoment(row), wall) || sameMinute(reportMoment(row), at)) ??
        (byPosition ? sortedRows[index] : undefined) ??
        null;

      const zone =
        schedule.scheduleTimeZone && schedule.scheduleTimeZone !== VIEWER_ZONE
          ? schedule.scheduleTimeZone
          : null;

      sends.push({
        key: `${message.id}-${schedule.id ?? index}`,
        message,
        schedule,
        subject: subjectOf(message),
        at,
        when: formatWhen(at, now),
        exact: exactSchedule(schedule),
        zone,
        past: !!at && at.getTime() <= now.getTime(),
        status: String(schedule.status || match?.scheduleStatus || ''),
        report: match,
      });
    });
  }

  return sends.sort((a, b) => (a.at?.getTime() ?? Infinity) - (b.at?.getTime() ?? Infinity));
}

/** The next send still to go out, if any. */
export function nextSend(sends: CampaignSend[]): CampaignSend | null {
  return sends.find((send) => !send.past && !!send.at && !isStopped(send.status)) ?? null;
}

/** A send that will not go out: cancelled, stopped, failed or abandoned. */
export function isStopped(status: string | null | undefined): boolean {
  return /cancel|stop|abandon|fail|disabled/i.test(status ?? '');
}

export function countdownTo(at: Date | null, now = new Date()): string {
  return formatCountdown(at, now);
}

/**
 * What bk-delivery needs for a campaign message: it reports on sent messages
 * only, so a message counts as sent once any of its sends has gone out.
 */
export function messageDelivery(
  message: EngagementMessage,
  now = new Date()
): { status: string; sentAt: Date | null } {
  const past = schedulesOf(message)
    .map((schedule) => scheduleMoment(schedule))
    .filter((at): at is Date => !!at && at.getTime() <= now.getTime())
    .sort((a, b) => b.getTime() - a.getTime());
  if (past.length) return { status: 'completed', sentAt: past[0] };
  // "Active" on a campaign message means it is live in the campaign, not that
  // it went out, so only a completed message counts as sent without a past send.
  const status = String(message.status ?? '');
  return { status: /^completed$/i.test(status) ? 'completed' : 'pending', sentAt: toDate(message.sendAt) };
}

export function subjectOf(message: Partial<EngagementMessage> | null | undefined): string {
  return String(message?.messageSubject ?? '').trim() || 'Untitled message';
}

/** "Runs 12 Oct – 30 Nov 2026", "Starts 12 Oct 2026", or "" with no dates. */
export function runWindow(start: string | null | undefined, end: string | null | undefined): string {
  const from = toDay(start);
  const to = toDay(end);
  if (from && to) {
    const sameYear = from.getFullYear() === to.getFullYear();
    const left = new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      ...(sameYear ? {} : { year: 'numeric' }),
    }).format(from);
    return `Runs ${left} – ${dayLabel(to)}`;
  }
  if (from) return `Starts ${dayLabel(from)}`;
  if (to) return `Ends ${dayLabel(to)}`;
  return '';
}

/** A date-only string read as a local day, so "2026-10-12" never shifts a day. */
function toDay(value: string | null | undefined): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  return toDate(value ?? null);
}

function dayLabel(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

export function plural(count: number | null | undefined, one: string, many: string): string {
  return `${formatCount(count ?? 0)} ${(count ?? 0) === 1 ? one : many}`;
}

/** "a, b and c". */
export function listJoin(items: string[]): string {
  const list = items.filter(Boolean);
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}
